import He from 'he';
import { DOMParser, HTMLElement as LinkedomHTMLElement, Text as LinkedomText } from 'linkedom';
import omit from 'lodash/omit';
import { clone } from 'ramda';
import type { Editor, Location } from 'slate';
import { v4 as uuid4 } from 'uuid';

import { ALINE_DRAFT_DATA_ATTRIBUTES, getAlineDraftListMarkerStyleName } from '../../agent-harness';
import { linkColor } from '../../constants';
import { LIST_ATTRIBUTE_NAMES } from '../../extract-html/list-attributes';
import type { CastParagraph } from '../../extract-html/types';
import type {
  AlignmentType,
  AlineContentBlock,
  AlineText,
  AlineTextFormats,
  GlobalStylesMap,
  LinkElement,
  ListDefinition,
  ListLevel,
  ListLevelDefinition,
  ParagraphElement,
  ParagraphStyleVariant,
  TableCellElement,
  TableElement,
  TableRowElement,
  TextPropsWithoutComments,
  VerticalAlign,
} from '../../slate-api';
import {
  createDefaultBreak,
  createDefaultParagraph,
  createListDefinition,
  createListMarkerStyles,
  defaultTableBorders,
  generateLocationId,
  isBreakElement,
  isListLevel,
  isParagraphElement,
  ListMarkerStyleName,
  lucidaSansTypewriterFamily,
  lucidaSansTypewriteType,
  Marks,
  noTableBorders,
  Queries,
  resolveFontFromCssFamily,
  tableCellType,
  tableRowType,
  tableType,
} from '../../slate-api';
import type { UUID } from '../../types';
import { pxToPt } from '../../util';

/**
 * NOTE: Refer to [HTML to Aline README](./HTML_TO_ALINE.md) for more
 * information. about how this works.
 */

/**
 * Fixed paragraph-spacing fallbacks (in points) for content produced from
 * HTML.
 *
 * The document stylesheet defines text formatting per style variant but NOT
 * paragraph spacing — `spacingAfter`/`spacingBefore`/`lineSpacing` live
 * directly on each paragraph with no stylesheet default. So these are only a
 * last resort: when an insertion location is known, paragraphs first borrow
 * spacing from the nearest matching paragraph in the document (see
 * `fitParagraphSpacingToNearbyContent`); these fixed values apply only when no
 * such neighbor exists.
 */
const HTML_PARAGRAPH_SPACING_PT = {
  /** Default gap after a paragraph produced from HTML. */
  default: 6,
  /** No gap after the empty paragraph a page break leaves behind. */
  pageBreak: 0,
};

type HtmlBaseVariant = 'style-normal' | 'style-heading1' | 'style-heading2' | 'style-heading3';

/**
 * Base text marks for a paragraph variant, read from the document's active
 * stylesheet so content built from HTML matches the document's own styles
 * (e.g. its `style-normal` font and size) instead of a hardcoded global
 * default. Falls back to the built-in marks if the stylesheet lacks the
 * variant.
 *
 * This only sets the *base* (INHERITED) marks. Formatting that is explicit in
 * the HTML (bold, colored or sized spans) is layered on top as USER_APPLIED
 * and always wins. See the `htmlToAline` seeding site for when this base is
 * used versus inheriting from the cursor.
 *
 * `Marks` is read lazily here (not in a module-level constant) to avoid a
 * circular-import initialization order problem with the slate-api barrel.
 */
function getVariantBaseFormats(
  styleSheet: GlobalStylesMap,
  variant: HtmlBaseVariant
): AlineTextFormats {
  if (variant in styleSheet) return styleSheet[variant].styles;
  const builtInMarks: Record<HtmlBaseVariant, AlineTextFormats> = {
    'style-heading1': Marks.heading1,
    'style-heading2': Marks.heading2,
    'style-heading3': Marks.heading3,
    'style-normal': Marks.default,
  };
  return builtInMarks[variant];
}

/**
 * Tags that are generated when doing cut-and-paste from MS Word.
 * - o:p: Office paragraph marker
 * - w:t: Word text run
 * - m:t: Math text
 * - v:textbox: Vector text box
 * - st1:*: Smart tags (various types like date, place, etc.)
 */
const officeInlineTags = ['o:p', 'w:t', 'm:t', 'v:textbox', 'st1:*'];

/**
 * HTML inline tags.
 */
const standardInlineTags = ['a', 'b', 'br', 'i', 'span', 'strong', 'em', 'u', 's', 'del'];

/**
 * List definition preset indices.
 */
const listIndentationFirstLine = -0.25;
const listIndentationLeftIncrement = 0.5;

/**
 * Synthetic source id that groups the editor's symmetric `<p list-level/
 * list-type>` items into one list within a single conversion. A real list id
 * is minted from it; during a redline insert the inserted list may then adopt
 * the anchor paragraph's list id instead, to continue an existing list (see
 * `applyInsertOutput`).
 */
const SYMMETRIC_LIST_SOURCE_ID = 'editor-symmetric-list';

/**
 * Special characters that indicate intentional empty content in HTML.
 * These are typically used by word processors and HTML editors to represent
 * empty paragraphs or maintain specific formatting.
 *
 * We detect these to preserve empty paragraphs, but remove them since
 * they aren't supported in the Aline editor.
 */
const SPECIAL_CHARS = {
  // Narrow no-break space.
  NARROW_NBSP: '\u202F',
  // Non-breaking space.
  NBSP: '\u00A0',
  // Zero-width space
  ZWSP: '\u200B',
} as const;

/**
 * Tests if a string contains any special characters that indicate
 * intentional empty content.
 */
function hasSpecialChars(text: string): boolean {
  return Object.values(SPECIAL_CHARS).some(char => text.includes(char));
}

/**
 * Clean text content for insertion into Aline.
 * Returns both the cleaned text and whether the original contained special characters
 * that indicate intentional empty content.
 */
function cleanTextContent(text: string): { shouldPreserve: boolean; text: string } {
  // Decode converts HTML entities to their corresponding Unicode characters.
  const decoded = He.decode(text);
  const shouldPreserve = hasSpecialChars(decoded);

  /**
   * Browser clipboard HTML (especially from Word) can contain literal newlines inside text nodes
   * (Firefox) for "real" spaces. Normalize those to regular spaces.
   *
   * IMPORTANT: DOMParser also creates whitespace-only Text nodes between block elements when HTML
   * is pretty-printed. After normalization, those would become a single " " and would incorrectly
   * be inserted as real content. So we drop whitespace-only nodes after normalization.
   */
  const cleaned = decoded
    .replace(/\s*\r?\n\s*/g, ' ')
    .replace(new RegExp(`[${Object.values(SPECIAL_CHARS).join('')}]`, 'g'), '') // Remove special characters
    .replace(/[ \t\f\v]+/g, ' '); // Collapse multiple regular spaces into a single space.

  // Drop whitespace-only nodes (but keep shouldPreserve so empty paragraphs can still be preserved).
  if (cleaned.trim() === '') return { shouldPreserve, text: '' };

  /**
   * Avoid introducing leading/trailing spaces when the original node starts/ends with newlines
   * (pretty-printed HTML).
   */
  if (/^\s*\r?\n/.test(decoded)) return { shouldPreserve, text: cleaned.trimStart() };
  if (/\r?\n\s*$/.test(decoded)) return { shouldPreserve, text: cleaned.trimEnd() };

  return { shouldPreserve, text: cleaned };
}

/**
 * Clean text content from <pre> elements for insertion into Aline.
 * Preserves newlines (which will be split into separate paragraphs) but decodes HTML entities.
 * Returns both the cleaned text and whether the original contained special characters.
 */
function cleanPreTextContent(text: string): { shouldPreserve: boolean; text: string } {
  // Decode converts HTML entities to their corresponding Unicode characters.
  const decoded = He.decode(text);
  const shouldPreserve = hasSpecialChars(decoded);
  // For <pre> content, we preserve newlines but remove special characters
  const cleaned = decoded.replace(
    new RegExp(`[${Object.values(SPECIAL_CHARS).join('')}]`, 'g'),
    ''
  );

  return { shouldPreserve, text: cleaned };
}

/**
 * Reads the text formatting off an HTML node and layers it onto `defaultMarks`
 * as `USER_APPLIED` overrides.
 *
 * When `preserveHtmlTextStyles` is false (the paste path), source color is
 * ignored and unsupported fonts stay on the destination; font size and
 * supported faces are kept when they differ from the destination. Emphasis
 * (bold/italic/underline/strikethrough) and super/subscript are kept either
 * way. The LLM/redline path leaves this true so it can honor any styling the
 * model emits.
 */
function getMarks(
  text: Node,
  defaultMarks: TextPropsWithoutComments,
  preserveHtmlTextStyles: boolean
): AlineTextFormats {
  // Ensure to copy object so we don't mutate defaultMarks.
  const marks = clone(defaultMarks);
  const color = getStyle(text, 'color');
  const fontSize = getStyle(text, 'font-size');
  const fontWeight = getStyle(text, 'font-weight');
  const fontFamily = getStyle(text, 'font-family');
  const fontStyle = getStyle(text, 'font-style');
  const textDecoration = getStyle(text, 'text-decoration');
  const textDecorations = (textDecoration ?? '').split(' ');
  const verticalAlign = getStyle(text, 'vertical-align');

  // Check for bold formatting
  if (
    ['700', '800', '900', 'bold', 'bolder'].includes(fontWeight ?? '') ||
    hasParentTag(text, ['strong', 'b'])
  )
    marks.bold = { active: true, origin: 'USER_APPLIED' };

  if (preserveHtmlTextStyles && color) marks.fontColor = { color, origin: 'USER_APPLIED' };

  const parsedFontSize = fontSize ? getCssLengthInPoints(fontSize) : null;
  if (parsedFontSize) {
    if (preserveHtmlTextStyles || parsedFontSize !== defaultMarks.fontSize.size) {
      marks.fontSize = {
        origin: 'USER_APPLIED',
        size: parsedFontSize,
      };
    }
  }

  if (fontFamily) {
    const matchedFont = resolveFontFromCssFamily(fontFamily);
    if (
      matchedFont &&
      (preserveHtmlTextStyles || matchedFont.type !== defaultMarks.fontType.type)
    ) {
      marks.fontType = {
        fontFamily: matchedFont.fontFamily,
        origin: 'USER_APPLIED',
        type: matchedFont.type,
      };
    }
  }

  if (['italic', 'oblique'].includes(fontStyle ?? '') || hasParentTag(text, ['em', 'i']))
    marks.italic = { active: true, origin: 'USER_APPLIED' };

  if (textDecorations.includes('underline') || hasParentTag(text, ['u']))
    marks.underline = { active: true, origin: 'USER_APPLIED' };

  if (textDecorations.includes('line-through') || hasParentTag(text, ['s', 'del']))
    marks.strikethrough = { active: true, origin: 'USER_APPLIED' };

  if (verticalAlign) {
    const align: VerticalAlign =
      verticalAlign === 'super' || verticalAlign === 'sub' ? `${verticalAlign}script` : 'baseline';
    marks.verticalAlign = { align, origin: 'USER_APPLIED' };
  }

  return marks;
}

type LinkedomElementNode = Element & {
  style: Pick<CSSStyleDeclaration, 'getPropertyValue'>;
};
type LinkedomTextNode = Node & Pick<Text, 'textContent'>;

function getStyle(node: Node, style: string): string | null {
  if (getIsHtmlElementNode(node) && getStyleProperty(node, style))
    return getStyleProperty(node, style);

  if (node.parentNode) return getStyle(node.parentNode, style);

  return null;
}

function getIsHtmlElementNode(node: unknown): node is LinkedomElementNode {
  return node instanceof LinkedomHTMLElement;
}

function getIsTextNode(node: unknown): node is LinkedomTextNode {
  return node instanceof LinkedomText;
}

function getStyleProperty(node: LinkedomElementNode, propertyName: string) {
  return node.style.getPropertyValue(propertyName) ?? '';
}

function getTagName(node: LinkedomElementNode) {
  return node.tagName.toLowerCase();
}

function hasTagName(node: unknown, tagNames: string[]): node is LinkedomElementNode {
  return getIsHtmlElementNode(node) && tagNames.includes(getTagName(node));
}

function isTable(node: LinkedomElementNode) {
  return hasTagName(node, ['table']);
}

function isTableCell(node: unknown): node is LinkedomElementNode {
  return hasTagName(node, ['td', 'th']);
}

function isHeading(node: LinkedomElementNode) {
  return hasTagName(node, ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
}

function isBlockquote(node: LinkedomElementNode) {
  return hasTagName(node, ['blockquote']);
}

function isUnorderedList(node: LinkedomElementNode) {
  return hasTagName(node, ['ul']);
}

function isOrderedList(node: LinkedomElementNode) {
  return hasTagName(node, ['ol']);
}

function isList(node: LinkedomElementNode) {
  return isUnorderedList(node) || isOrderedList(node);
}

function isListItem(node: LinkedomElementNode) {
  return hasTagName(node, ['li']);
}

function getHeadingVariant(node: LinkedomElementNode): ParagraphStyleVariant {
  switch (node.tagName.toLowerCase()) {
    case 'h1':
      return 'style-heading1';
    case 'h2':
      return 'style-heading2';
    default:
      return 'style-heading3';
  }
}

function isInline(node: Node): boolean {
  return hasTagName(node, [...standardInlineTags, ...officeInlineTags]);
}

function isLink(node: LinkedomElementNode) {
  return hasTagName(node, ['a']) && Boolean(getLinkHref(node));
}

function isBreak(node: LinkedomElementNode) {
  return hasTagName(node, ['br']);
}

function isPageBreak(node: LinkedomElementNode) {
  return getBooleanDataAttribute(node, ALINE_DRAFT_DATA_ATTRIBUTES.pageBreak);
}

function isBlockWithParagraphStyles(node: LinkedomElementNode): boolean {
  const tagName = getTagName(node);
  if (
    [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'table',
      'thead',
      'tbody',
      'tr',
      'td',
      'th',
      'ol',
      'ul',
      'li',
      'pre',
    ].includes(tagName)
  )
    return false;

  return Boolean(
    getStyleProperty(node, 'text-align') ||
    getStyleProperty(node, 'margin-top') ||
    getStyleProperty(node, 'margin-bottom') ||
    getStyleProperty(node, 'background-color')
  );
}

function isPre(node: LinkedomElementNode) {
  return hasTagName(node, ['pre']);
}

function getLinkHref(node: Node) {
  if (!getIsHtmlElementNode(node)) return '';

  return node.getAttribute('href')?.trim() ?? '';
}

function getDataAttribute(node: LinkedomElementNode, attributeName: string) {
  return node.getAttribute(attributeName)?.trim() ?? '';
}

function getBooleanDataAttribute(node: LinkedomElementNode, attributeName: string) {
  return getDataAttribute(node, attributeName).toLowerCase() === 'true';
}

function hasParentTag(node: Node, tags: string[]): boolean {
  const nodeState: { current: Node | null } = { current: node };

  while (nodeState.current) {
    if (
      getIsHtmlElementNode(nodeState.current) &&
      tags.includes(nodeState.current.tagName.toLowerCase())
    )
      return true;

    nodeState.current = nodeState.current.parentNode;
  }

  return false;
}

type PartialParagraph = Omit<ParagraphElement, 'children'>;

export function htmlToAline(
  editor: Editor,
  options: {
    html: string;
    /**
     * Where the produced blocks will be inserted. When provided, paragraphs
     * borrow their spacing from the nearest matching paragraph there (see
     * `fitParagraphSpacingToNearbyContent`). This is opt-in: callers that
     * actually insert pass it (paste passes the selection, the redline/AI path
     * passes the anchor location, since it inserts at a computed path rather
     * than the selection). Plain conversion callers omit it and keep the
     * default spacing — the serializer on its own does not infer a location
     * from the editor's selection.
     *
     * This only exists because paragraph spacing isn't part of the global
     * styles yet. If spacing moves into the stylesheet (see
     * `HTML_PARAGRAPH_SPACING_PT`), this could likely be removed in favor of
     * reading spacing per variant straight from the document styles.
     */
    at?: Location;
    /**
     * Whether to honor the source HTML's text *appearance* (font family, size,
     * color). Defaults to true so the LLM/redline path can use any styling the
     * model emits. The paste path passes false so pasted text adopts the
     * document's styling instead of the source's — see `getMarks`.
     */
    preserveHtmlTextStyles?: boolean;
  }
): { blocks: AlineContentBlock[]; listDefinitions: Record<UUID, ListDefinition> } {
  const { at, preserveHtmlTextStyles = true } = options;
  const styleSheet = Queries.activeStyleSheet(editor);
  /**
   * Base text formatting for the inserted content:
   * - If there is a cursor/selection, inherit the surrounding text's formats
   *   (`formatsAtCursor`) so manual paste matches the text it is dropped into.
   * - Otherwise (no selection — e.g. the redline / AI insertion path, which
   *   inserts at a computed path rather than the selection), fall back to the
   *   document's own `style-normal` from the stylesheet, NOT a hardcoded
   *   global default, so inserted text matches the document's font and size.
   */
  const formats = Queries.formatsAtCursor(editor);
  const document = new DOMParser().parseFromString(
    `<!doctype html><html><body>${options.html}</body></html>`,
    'text/html'
  );
  document.body.normalize();

  const nextParagraph: PartialParagraph = omit(createDefaultParagraph(), ['children']);
  const result = convertNodeToBlocks(document.body, {
    blocks: [],
    editor,
    mutableLegalListState: createDefaultLegalListState(),
    mutableListDefinitions: {},
    mutableListLevelTypes: null,
    nextFormats: formats ?? {
      ...getVariantBaseFormats(styleSheet, 'style-normal'),
      suggestedEdits: [],
    },
    nextListDefinition: null,
    nextParagraph,
    paragraph: null,
    preserveHtmlTextStyles,
    styleSheet,
  });

  if (at) fitParagraphSpacingToNearbyContent(editor, result.blocks, at);

  return result;
}

/**
 * Replaces each top-level paragraph's default spacing with the spacing of the
 * nearest existing paragraph that shares its style variant (see
 * `Queries.findNearestParagraphByStyleVariant`). A fixed default is often
 * wrong for a given document, so borrowing from a real neighbor makes the
 * insertion blend in. Paragraphs keep the `HTML_PARAGRAPH_SPACING_PT` fallback
 * set during conversion when there is no matching neighbor.
 *
 * EXCEPTION — tables: paragraphs inside an inserted table are intentionally
 * left untouched. Table cells have their own (near-zero) spacing conventions,
 * so giving them body-paragraph spacing would look wrong. This loop only walks
 * the TOP-LEVEL blocks, so table-cell paragraphs (nested inside a table block)
 * are never reached. Do not "fix" this by recursing into tables.
 */
function fitParagraphSpacingToNearbyContent(
  editor: Editor,
  blocks: AlineContentBlock[],
  at: Location
): void {
  type ParagraphSpacing = Pick<ParagraphElement, 'lineSpacing' | 'spacingAfter' | 'spacingBefore'>;
  const spacingByVariant = new Map<ParagraphStyleVariant, ParagraphSpacing | null>();

  for (const block of blocks) {
    if (!isParagraphElement(block)) continue;
    /**
     * Page/line breaks rely on their own explicit spacing (e.g. 0 after a page
     * break), so leave them alone.
     */
    if (block.children.some(isBreakElement)) continue;

    const styleVariant = Queries.renderedParagraphStyleVariant(block);
    if (!spacingByVariant.has(styleVariant)) {
      const match = Queries.findNearestParagraphByStyleVariant(editor, { at, styleVariant });
      const [matchParagraph] = match ?? [];
      spacingByVariant.set(
        styleVariant,
        matchParagraph
          ? {
              lineSpacing: matchParagraph.lineSpacing,
              spacingAfter: matchParagraph.spacingAfter,
              spacingBefore: matchParagraph.spacingBefore,
            }
          : null
      );
    }

    const spacing = spacingByVariant.get(styleVariant);
    if (spacing) {
      block.lineSpacing = spacing.lineSpacing;
      block.spacingAfter = spacing.spacingAfter;
      block.spacingBefore = spacing.spacingBefore;
    }
  }
}

type ListLevelType = 'ul' | 'ol';
type ListLevelTypes = [
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
  ListLevelType,
];

type LegalListState = {
  idBySourceId: Map<string, UUID>;
  markerFormatsBySourceId: Map<string, Partial<AlineTextFormats>[]>;
  markerStyleNamesBySourceId: Map<string, ListMarkerStyleName[]>;
};

/**
 * Please leaves these notes!
 *
 * TraversalState is an unintuitive combination of the equivalent of arguments
 * that are passed down, and immutable results which are passed up and a
 * reference to a mutable paragraph which is passed across.
 *
 * - Passed down
 *   - nextFormats: The text formats to apply to the next text to create
 *   - nextParagraph: The props for the next paragraph to create.
 *
 * - Passed across
 *   - paragraph: The current paragraph being built or null if there isn't one
 *
 *  - Accumulated
 *   - mutableListDefinitions: The array of list definitions which is mutable.
 *
 * - Passed up and passed down
 *   - blocks: The final blocks which is immutable.
 *
 */
type Context = {
  blocks: AlineContentBlock[];
  editor: Editor;
  mutableLegalListState: LegalListState;
  mutableListDefinitions: Record<UUID, ListDefinition>;
  mutableListLevelTypes: ListLevelTypes | null;
  nextFormats: TextPropsWithoutComments;
  nextListDefinition: ListDefinition | null;
  nextParagraph: Omit<ParagraphElement, 'children'>;
  paragraph: ParagraphElement | null;
  /**
   * Whether to keep the source HTML's appearance styling (font family, size,
   * color). The paste path sets this false so text pasted from a web page takes
   * on this document's styling instead of dragging the page's own font/size/
   * color in and looking out of place. See {@link getMarks} for what's kept
   * either way (emphasis, super/subscript).
   */
  preserveHtmlTextStyles: boolean;
  styleSheet: GlobalStylesMap;
};

function normalizeListLevel(listLevel: number): ListLevel {
  const value = Math.min(listLevel, 8);
  if (!isListLevel(value)) throw new Error('Not a valid list level value.');
  return value;
}

function createDefaultLegalListState(): LegalListState {
  return {
    idBySourceId: new Map(),
    markerFormatsBySourceId: new Map(),
    markerStyleNamesBySourceId: new Map(),
  };
}

function createMarkerStyleNames(defaultStyleName: ListMarkerStyleName) {
  return [
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
    defaultStyleName,
  ];
}

function createListDefinitionFromMarkerStyleNames(
  markerStyleNames: ListMarkerStyleName[],
  markerFormatsByLevel: Partial<AlineTextFormats>[]
) {
  const listStyles = createListMarkerStyles({
    names: [markerStyleNames[0] ?? ListMarkerStyleName.DECIMAL, ...markerStyleNames.slice(1)],
  });

  return {
    0: createListLevelDefinition(listStyles[0], markerFormatsByLevel[0]),
    1: createListLevelDefinition(listStyles[1], markerFormatsByLevel[1]),
    2: createListLevelDefinition(listStyles[2], markerFormatsByLevel[2]),
    3: createListLevelDefinition(listStyles[3], markerFormatsByLevel[3]),
    4: createListLevelDefinition(listStyles[4], markerFormatsByLevel[4]),
    5: createListLevelDefinition(listStyles[5], markerFormatsByLevel[5]),
    6: createListLevelDefinition(listStyles[6], markerFormatsByLevel[6]),
    7: createListLevelDefinition(listStyles[7], markerFormatsByLevel[7]),
    8: createListLevelDefinition(listStyles[8], markerFormatsByLevel[8]),
  } satisfies ListDefinition;
}

function createListLevelDefinition(
  style: ListLevelDefinition['style'],
  markerFormats: Partial<AlineTextFormats> | undefined
): ListLevelDefinition {
  return markerFormats ? { markerFormats, style } : { style };
}

/**
 * List props for a `<p>` carrying list metadata, in either of two shapes:
 * - the drafter's `data-aline-list-id` / `data-aline-list-level` (0-based) /
 *   `data-aline-list-style`; or
 * - the editor's symmetric `list-level` (1-based) / `list-type`, the same
 *   attributes it reads in the document context, so it writes them back. These
 *   carry no list id — a redline insert may adopt the anchor's list afterward
 *   (continuation), and a standalone list gets a fresh id like any other.
 *
 * Returns null when the node has neither shape.
 */
function getLegalListParagraphProps(
  node: LinkedomElementNode,
  state: Context
): PartialParagraph | null {
  const draftListId = getDataAttribute(node, ALINE_DRAFT_DATA_ATTRIBUTES.listId);
  if (draftListId) {
    const rawListLevel = Number.parseInt(
      getDataAttribute(node, ALINE_DRAFT_DATA_ATTRIBUTES.listLevel),
      10
    );
    if (!Number.isFinite(rawListLevel) || rawListLevel < 0) return null;

    return buildLegalListParagraphProps(node, state, {
      listLevel: normalizeListLevel(rawListLevel),
      markerStyleName: getAlineDraftListMarkerStyleName(
        getDataAttribute(node, ALINE_DRAFT_DATA_ATTRIBUTES.listStyle)
      ),
      sourceListId: draftListId,
    });
  }

  const rawSymmetricLevel = getDataAttribute(node, LIST_ATTRIBUTE_NAMES.listLevel);
  if (rawSymmetricLevel) {
    // `list-level` is 1-based here (1 = top level), unlike the 0-based draft level.
    const oneBasedLevel = Number.parseInt(rawSymmetricLevel, 10);
    if (!Number.isFinite(oneBasedLevel) || oneBasedLevel < 1) return null;

    return buildLegalListParagraphProps(node, state, {
      listLevel: normalizeListLevel(oneBasedLevel - 1),
      markerStyleName: getListMarkerStyleNameForType(
        getDataAttribute(node, LIST_ATTRIBUTE_NAMES.listType)
      ),
      sourceListId: SYMMETRIC_LIST_SOURCE_ID,
    });
  }

  return null;
}

function buildLegalListParagraphProps(
  node: LinkedomElementNode,
  state: Context,
  {
    listLevel,
    markerStyleName,
    sourceListId,
  }: { listLevel: ListLevel; markerStyleName: ListMarkerStyleName; sourceListId: string }
): PartialParagraph {
  const listId = getOrCreateLegalListId(sourceListId, state);
  setLegalListLevelStyle(
    sourceListId,
    listId,
    listLevel,
    markerStyleName,
    getLegalListMarkerFormats(node, state),
    state
  );

  return {
    ...createParagraphPropsFromElement({
      baseParagraph: state.nextParagraph,
      node,
      preserveHtmlTextStyles: state.preserveHtmlTextStyles,
    }),
    indentationFirstLine: listIndentationFirstLine,
    indentationLeft: listIndentationLeftIncrement * (listLevel + 1),
    listProps: {
      listId,
      listLevel,
    },
  };
}

const BULLET_LIST_TYPE = 'bullet' satisfies NonNullable<CastParagraph['listType']>;

/**
 * Maps the editor's `list-type` (`bullet` / `numbered`) to a marker style,
 * matching the `<ul>`/`<ol>` defaults.
 */
function getListMarkerStyleNameForType(listType: string): ListMarkerStyleName {
  return listType.trim().toLowerCase() === BULLET_LIST_TYPE
    ? ListMarkerStyleName.BLACK_CIRCLE
    : ListMarkerStyleName.DECIMAL;
}

function getOrCreateLegalListId(sourceListId: string, state: Context) {
  const existingListId = state.mutableLegalListState.idBySourceId.get(sourceListId);
  if (existingListId) return existingListId;

  const listId = uuid4();
  state.mutableLegalListState.idBySourceId.set(sourceListId, listId);
  return listId;
}

function setLegalListLevelStyle(
  sourceListId: string,
  listId: UUID,
  listLevel: ListLevel,
  markerStyleName: ListMarkerStyleName,
  markerFormats: Partial<AlineTextFormats>,
  state: Context
) {
  const markerStyleNames =
    state.mutableLegalListState.markerStyleNamesBySourceId.get(sourceListId) ??
    createMarkerStyleNames(ListMarkerStyleName.DECIMAL);
  markerStyleNames[listLevel] = markerStyleName;
  state.mutableLegalListState.markerStyleNamesBySourceId.set(sourceListId, markerStyleNames);

  const markerFormatsByLevel =
    state.mutableLegalListState.markerFormatsBySourceId.get(sourceListId) ?? [];
  markerFormatsByLevel[listLevel] = markerFormats;
  state.mutableLegalListState.markerFormatsBySourceId.set(sourceListId, markerFormatsByLevel);

  state.mutableListDefinitions[listId] = createListDefinitionFromMarkerStyleNames(
    markerStyleNames,
    markerFormatsByLevel
  );
}

function getLegalListMarkerFormats(node: LinkedomElementNode, state: Context) {
  return omit(
    getMarks(node, getLegalListBaseMarkerFormats(node, state), state.preserveHtmlTextStyles),
    ['suggestedEdits']
  );
}

function getLegalListBaseMarkerFormats(
  node: LinkedomElementNode,
  state: Context
): TextPropsWithoutComments {
  if (!isHeading(node)) return state.nextFormats;

  const variant = getHeadingVariant(node);
  if (variant === 'style-heading1')
    return { ...state.nextFormats, ...getVariantBaseFormats(state.styleSheet, 'style-heading1') };
  if (variant === 'style-heading2')
    return { ...state.nextFormats, ...getVariantBaseFormats(state.styleSheet, 'style-heading2') };
  return { ...state.nextFormats, ...getVariantBaseFormats(state.styleSheet, 'style-heading3') };
}

function getNextListInfo(
  state: Context,
  inc: number,
  defaultListLevelType: ListLevelType
): {
  isNewList: boolean;
  listProps: NonNullable<ParagraphElement['listProps']>;
  mutableListLevelTypes: ListLevelTypes;
} {
  if (state.nextParagraph.listProps) {
    if (!state.mutableListLevelTypes) {
      throw new Error('mutableListLevelTypes is required.');
    }
    return {
      isNewList: false,
      listProps: {
        listId: state.nextParagraph.listProps.listId,
        listLevel: normalizeListLevel(state.nextParagraph.listProps.listLevel + inc),
      },
      mutableListLevelTypes: state.mutableListLevelTypes,
    };
  }
  return {
    isNewList: true,
    listProps: {
      listId: uuid4(),
      listLevel: 0,
    },
    mutableListLevelTypes: [
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
      defaultListLevelType,
    ],
  };
}

function createParagraphPropsFromElement({
  baseParagraph,
  node,
  preserveHtmlTextStyles,
}: {
  baseParagraph: PartialParagraph;
  node: LinkedomElementNode;
  preserveHtmlTextStyles: boolean;
}): PartialParagraph {
  const nextParagraph = { ...baseParagraph };
  const alignment = getAlignment(getStyleProperty(node, 'text-align'));
  if (alignment) nextParagraph.alignment = alignment;

  const spacingBefore = getCssLengthInPoints(getStyleProperty(node, 'margin-top'));
  if (spacingBefore != null) nextParagraph.spacingBefore = spacingBefore;

  const spacingAfter = getCssLengthInPoints(getStyleProperty(node, 'margin-bottom'));
  if (spacingAfter != null) nextParagraph.spacingAfter = spacingAfter;

  if (preserveHtmlTextStyles) {
    const backgroundColor = normalizeCssColor(getStyleProperty(node, 'background-color').trim());
    if (backgroundColor) nextParagraph.shade = { fillColor: backgroundColor };
  }

  return nextParagraph;
}

function getTableCellShade(node: LinkedomElementNode): TableCellElement['shade'] {
  const backgroundColor = normalizeCssColor(getStyleProperty(node, 'background-color').trim());
  if (!backgroundColor) return undefined;

  return { fillColor: backgroundColor };
}

function getAlignment(value: string): AlignmentType | null {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'left' ||
    normalized === 'center' ||
    normalized === 'right' ||
    normalized === 'justify'
  )
    return normalized;

  return null;
}

function normalizeCssColor(value: string) {
  const hexMatch = value.match(/^#([0-9a-f]{6})$/i);
  const hexValue = hexMatch?.[1];
  if (!hexValue) return value;

  const red = Number.parseInt(hexValue.slice(0, 2), 16);
  const green = Number.parseInt(hexValue.slice(2, 4), 16);
  const blue = Number.parseInt(hexValue.slice(4, 6), 16);
  return `rgb(${red}, ${green}, ${blue})`;
}

function getCssLengthInPoints(value: string): number | null {
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)(px|pt)$/i);
  if (!match) return null;

  const valueText = match[1];
  const unit = match[2]?.toLowerCase();
  if (!valueText || !unit) return null;

  const numberValue = Number(valueText);
  if (!Number.isFinite(numberValue)) return null;

  return unit === 'px' ? pxToPt(numberValue) : numberValue;
}

function getCssLengthInInches(value: string, basisIn: number): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px|pt|in|%)$/i);
  if (!match) return null;

  const valueText = match[1];
  const unit = match[2]?.toLowerCase();
  if (!valueText || !unit) return null;

  const numberValue = Number(valueText);
  if (!Number.isFinite(numberValue)) return null;

  if (unit === '%') return basisIn * (numberValue / 100);
  if (unit === 'in') return numberValue;
  if (unit === 'pt') return numberValue / 72;
  return pxToPt(numberValue) / 72;
}

type TableCellWithSource = {
  columnSpan: number;
  element: TableCellElement;
  sourceNode: LinkedomElementNode | null;
  startColumnIndex: number;
};

type TableRowWithSources = {
  cells: TableCellWithSource[];
};

function getPositiveIntegerAttribute(node: LinkedomElementNode, attributeName: string) {
  const parsed = Number.parseInt(getDataAttribute(node, attributeName), 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return parsed;
}

function getTableCellColumnSpan(node: LinkedomElementNode) {
  return getPositiveIntegerAttribute(node, 'colspan');
}

function getTableCellRowSpan(node: LinkedomElementNode) {
  return getPositiveIntegerAttribute(node, 'rowspan');
}

function convertTableToElement(node: LinkedomElementNode, state: Context): TableElement {
  const contentWidthIn = Queries.activePageContentWidth(state.editor, 'in');
  const tableWidthIn =
    getCssLengthInInches(getStyleProperty(node, 'width'), contentWidthIn) ?? contentWidthIn;
  const rows = removeSpacerColumns(buildTableRows(node, state));
  const gridColumnCount = Math.max(1, getMaxGridColumnCount(rows));
  const defaultColumnWidth = tableWidthIn / gridColumnCount;

  return {
    align: 'left',
    borders: getIsBorderlessTableElement(node) ? noTableBorders : defaultTableBorders,
    children: rows.map((row): TableRowElement => {
      return {
        cantSplit: false,
        children: row.cells.map(({ columnSpan, element, sourceNode }) => {
          const explicitWidth = sourceNode
            ? getCssLengthInInches(getStyleProperty(sourceNode, 'width'), tableWidthIn)
            : null;
          return {
            ...element,
            width: explicitWidth ?? defaultColumnWidth * columnSpan,
          };
        }),
        locationId: generateLocationId(),
        suggestedEdits: [],
        type: tableRowType,
      };
    }),
    indent: { type: 'nil' },
    locationId: generateLocationId(),
    suggestedEdits: [],
    type: tableType,
    width: tableWidthIn,
  };
}

function buildTableRows(node: LinkedomElementNode, state: Context): TableRowWithSources[] {
  const activeRowSpans: number[] = [];
  return Array.from(node.getElementsByTagName('tr')).map(rowNode => {
    const row: TableRowWithSources = { cells: [] };
    let columnIndex = 0;

    for (const cellNode of Array.from(rowNode.children).filter(isTableCell)) {
      columnIndex = appendActiveRowSpanContinuations(row, activeRowSpans, columnIndex);

      const columnSpan = getTableCellColumnSpan(cellNode);
      const rowSpan = getTableCellRowSpan(cellNode);
      const tableCell = createTableCellElement(cellNode, state);
      if (columnSpan > 1) tableCell.columnSpan = columnSpan;
      if (rowSpan > 1) tableCell.verticalMerge = 'restart';

      row.cells.push({
        columnSpan,
        element: tableCell,
        sourceNode: cellNode,
        startColumnIndex: columnIndex,
      });

      if (rowSpan > 1) {
        for (let offset = 0; offset < columnSpan; offset += 1) {
          activeRowSpans[columnIndex + offset] = rowSpan - 1;
        }
      }
      columnIndex += columnSpan;
    }

    appendActiveRowSpanContinuations(row, activeRowSpans, columnIndex);
    return row;
  });
}

function appendActiveRowSpanContinuations(
  row: TableRowWithSources,
  activeRowSpans: number[],
  startColumnIndex: number
) {
  let columnIndex = startColumnIndex;
  while ((activeRowSpans[columnIndex] ?? 0) > 0) {
    row.cells.push({
      columnSpan: 1,
      element: {
        children: [],
        locationId: generateLocationId(),
        suggestedEdits: [],
        type: tableCellType,
        verticalMerge: 'continue',
      },
      sourceNode: null,
      startColumnIndex: columnIndex,
    });
    activeRowSpans[columnIndex] = (activeRowSpans[columnIndex] ?? 0) - 1;
    columnIndex += 1;
  }
  return columnIndex;
}

function createTableCellElement(cellNode: LinkedomElementNode, state: Context): TableCellElement {
  const tableCell: TableCellElement = {
    children: convertNodeToBlocks(cellNode, {
      blocks: [],
      editor: state.editor,
      mutableLegalListState: state.mutableLegalListState,
      mutableListDefinitions: state.mutableListDefinitions,
      mutableListLevelTypes: null,
      nextFormats: state.nextFormats,
      nextListDefinition: null,
      nextParagraph: createParagraphPropsFromElement({
        baseParagraph: state.nextParagraph,
        node: cellNode,
        preserveHtmlTextStyles: state.preserveHtmlTextStyles,
      }),
      paragraph: null,
      preserveHtmlTextStyles: state.preserveHtmlTextStyles,
      styleSheet: state.styleSheet,
    }).blocks,
    locationId: generateLocationId(),
    suggestedEdits: [],
    type: tableCellType,
  };
  if (state.preserveHtmlTextStyles) {
    const shade = getTableCellShade(cellNode);
    if (shade) tableCell.shade = shade;
  }
  if (getIsBorderlessTableElement(cellNode)) tableCell.borders = noTableBorders;

  return tableCell;
}

function getIsBorderlessTableElement(node: LinkedomElementNode) {
  const border = getStyleProperty(node, 'border');
  const borderStyle = getStyleProperty(node, 'border-style');
  const borderWidth = getStyleProperty(node, 'border-width');

  return (
    getIsNoBorderValue(border) || getIsNoBorderValue(borderStyle) || getIsNoBorderValue(borderWidth)
  );
}

function getIsNoBorderValue(value: string) {
  const normalized = value.trim().toLowerCase();
  return (
    normalized === '0' || normalized === '0px' || normalized === '0pt' || normalized === 'none'
  );
}

function getMaxGridColumnCount(rows: readonly TableRowWithSources[]) {
  return rows.reduce((maxColumnCount, row) => {
    const rowColumnCount = row.cells.reduce(
      (max, cell) => Math.max(max, cell.startColumnIndex + cell.columnSpan),
      0
    );
    return Math.max(maxColumnCount, rowColumnCount);
  }, 0);
}

function removeSpacerColumns(rows: TableRowWithSources[]): TableRowWithSources[] {
  const maxColumnCount = getMaxGridColumnCount(rows);
  const removableColumnIndexes = new Set<number>();
  for (let columnIndex = 0; columnIndex < maxColumnCount; columnIndex += 1) {
    if (rows.length > 0 && rows.every(row => getIsRemovableSpacerColumnCell(row, columnIndex))) {
      removableColumnIndexes.add(columnIndex);
    }
  }
  if (removableColumnIndexes.size === 0) return rows;

  return rows.map(row => {
    const cells = row.cells.filter(cell => !removableColumnIndexes.has(cell.startColumnIndex));
    return {
      cells: cells.map((cell, index) => ({
        ...cell,
        startColumnIndex: index,
      })),
    };
  });
}

function getIsRemovableSpacerColumnCell(row: TableRowWithSources, columnIndex: number) {
  const cell = row.cells.find(
    candidate => candidate.startColumnIndex === columnIndex && candidate.columnSpan === 1
  );
  if (!cell?.sourceNode) return false;
  if (getTagName(cell.sourceNode) === 'th') return false;
  if (getBooleanDataAttribute(cell.sourceNode, ALINE_DRAFT_DATA_ATTRIBUTES.preserveEmptyColumn))
    return false;
  if (getStyleProperty(cell.sourceNode, 'width')) return false;
  if (getTableCellShade(cell.sourceNode)) return false;
  if (cell.element.verticalMerge) return false;

  return (cell.sourceNode.textContent ?? '').trim() === '';
}

/**
 * NOTE: Refer to [HTML to Aline README](./HTML_TO_ALINE.md) for more
 * information about how this works. There are some unusual gotchas in here in
 * that some of the context travels down, some of it travels sideways and some
 * of it travels up.
 */

function convertNodeToBlocks(
  node: Node,
  context: Context
): { blocks: AlineContentBlock[]; listDefinitions: Record<UUID, ListDefinition> } {
  const finalState = Array.from(node.childNodes).reduce<Context>((state, childNode) => {
    if (getIsTextNode(childNode)) {
      const { shouldPreserve, text } = cleanTextContent(childNode.textContent ?? '');
      // Skip empty text nodes unless they contained special characters
      if (!text && !shouldPreserve) return state;

      const lastParagraph = findOrCreateLastParagraph(state);
      lastParagraph.children.push({
        ...getMarks(childNode, state.nextFormats, state.preserveHtmlTextStyles),
        comments: [],
        suggestedEdits: [],
        text,
      });

      state.paragraph = lastParagraph;

      return state;
    }

    const isNodeHTMLElement = getIsHtmlElementNode(childNode);
    if (!isNodeHTMLElement) return state;

    if (isPageBreak(childNode)) {
      const pageBreakParagraph = createDefaultParagraph({
        paraProps: {
          ...state.nextParagraph,
          children: [createDefaultBreak()],
          locationId: generateLocationId(),
          spacingAfter: HTML_PARAGRAPH_SPACING_PT.pageBreak,
        },
      });

      return {
        ...state,
        blocks: [...state.blocks, pageBreakParagraph],
        paragraph: null,
      } satisfies Context;
    }

    if (isInline(childNode)) {
      if (isBreak(childNode)) {
        const newParagraph = createDefaultParagraph({
          paraProps: {
            ...state.nextParagraph,
            children: [],
            locationId: generateLocationId(),
            spacingAfter: HTML_PARAGRAPH_SPACING_PT.default,
          },
        });

        return {
          ...state,
          blocks: [...state.blocks, newParagraph],
          paragraph: newParagraph,
        } satisfies Context;
      }

      const lastParagraph = findOrCreateLastParagraph(state);
      const nodesToPush = isLink(childNode)
        ? [
            {
              children: traverseInlineContent(
                childNode,
                state.nextFormats,
                state.preserveHtmlTextStyles
              ).map(applyAlineLinkTextFormatting),
              href: getLinkHref(childNode),
              suggestedEdits: [],
              type: 'link',
            } satisfies LinkElement,
          ]
        : traverseInlineContent(childNode, state.nextFormats, state.preserveHtmlTextStyles);
      lastParagraph.children.push(...nodesToPush);

      state.paragraph = lastParagraph;
      return state;
    }

    const legalListParagraphProps = getLegalListParagraphProps(childNode, state);
    if (legalListParagraphProps && !isHeading(childNode)) {
      const nextBlocks = convertNodeToBlocks(childNode, {
        ...state,
        nextParagraph: legalListParagraphProps,
        paragraph: null,
      }).blocks;

      return {
        ...state,
        blocks: nextBlocks,
        paragraph: null,
      } satisfies Context;
    }

    if (isBlockWithParagraphStyles(childNode)) {
      const nextBlocks = convertNodeToBlocks(childNode, {
        ...state,
        nextParagraph: createParagraphPropsFromElement({
          baseParagraph: state.nextParagraph,
          node: childNode,
          preserveHtmlTextStyles: state.preserveHtmlTextStyles,
        }),
        paragraph: null,
      }).blocks;

      return {
        ...state,
        blocks: nextBlocks,
        paragraph: null,
      } satisfies Context;
    }

    if (isTable(childNode)) {
      state.blocks.push(convertTableToElement(childNode, state));
      return state;
    }

    if (isHeading(childNode)) {
      const variant = getHeadingVariant(childNode);
      const headingParagraphProps =
        legalListParagraphProps ??
        createParagraphPropsFromElement({
          baseParagraph: state.nextParagraph,
          node: childNode,
          preserveHtmlTextStyles: state.preserveHtmlTextStyles,
        });
      switch (variant) {
        case 'style-heading1': {
          const nextBlocks = convertNodeToBlocks(childNode, {
            ...state,
            nextFormats: {
              ...state.nextFormats,
              ...getVariantBaseFormats(state.styleSheet, 'style-heading1'),
            },
            nextParagraph: {
              ...headingParagraphProps,
              variant,
            },
            paragraph: null,
          }).blocks;
          return {
            ...state,
            blocks: nextBlocks,
            paragraph: null,
          } satisfies Context;
        }
        case 'style-heading2': {
          const nextBlocks = convertNodeToBlocks(childNode, {
            ...state,
            nextFormats: {
              ...state.nextFormats,
              ...getVariantBaseFormats(state.styleSheet, 'style-heading2'),
            },
            nextParagraph: {
              ...headingParagraphProps,
              variant,
            },
            paragraph: null,
          }).blocks;
          return {
            ...state,
            blocks: nextBlocks,
            paragraph: null,
          } satisfies Context;
        }
        default: {
          const nextBlocks = convertNodeToBlocks(childNode, {
            ...state,
            nextFormats: {
              ...state.nextFormats,
              ...getVariantBaseFormats(state.styleSheet, 'style-heading3'),
            },
            nextParagraph: {
              ...headingParagraphProps,
              variant,
            },
            paragraph: null,
          }).blocks;
          return {
            ...state,
            blocks: nextBlocks,
            paragraph: null,
          } satisfies Context;
        }
      }
    }

    if (isBlockquote(childNode)) {
      const nextBlocks = convertNodeToBlocks(childNode, {
        ...state,
        nextParagraph: {
          ...state.nextParagraph,
          indentationLeft: state.nextParagraph.indentationLeft + 0.5,
        },
      }).blocks;
      return {
        ...state,
        blocks: nextBlocks,
      } satisfies Context;
    }

    if (isPre(childNode)) {
      // Get the text content from the <pre> element
      const { text } = cleanPreTextContent(childNode.textContent ?? '');

      /**
       * Split by newlines to create separate paragraphs.
       *
       * We use trimEnd to remove trailing newlines or else we end up with an
       * empty paragraph at the end. Don't use `trim()` or we lose meaningful
       * spaces at the beginning of the first line of text.
       */
      const lines = text.trimEnd().split('\n');

      // Create a paragraph for each line
      const preBlocks = lines.map(line => {
        const paragraph = createDefaultParagraph({
          paraProps: {
            ...state.nextParagraph,
            children: [
              {
                ...state.nextFormats,
                comments: [],
                fontType: {
                  // Monospace font formatting
                  fontFamily: lucidaSansTypewriterFamily,
                  origin: 'USER_APPLIED',
                  type: lucidaSansTypewriteType,
                },
                suggestedEdits: [],
                text: line,
              },
            ],
            locationId: generateLocationId(),
            spacingAfter: HTML_PARAGRAPH_SPACING_PT.default,
          },
        });
        return paragraph;
      });

      return {
        ...state,
        blocks: [...state.blocks, ...preBlocks],
        paragraph: null,
      } satisfies Context;
    }

    function createListDefinitionInEditor(nextListInfo: {
      isNewList: boolean;
      listProps: NonNullable<ParagraphElement['listProps']>;
      mutableListLevelTypes: ListLevelTypes;
    }) {
      const BULLET = ListMarkerStyleName.BLACK_CIRCLE;
      const NUMBER = ListMarkerStyleName.DECIMAL;
      const listMarkerStyleNames = nextListInfo.mutableListLevelTypes.map(type =>
        type === 'ul' ? BULLET : NUMBER
      );
      const listStyles = createListMarkerStyles({
        /**
         * The [0] and .slice are to satisfy the type checker.
         */
        names: [listMarkerStyleNames[0], ...listMarkerStyleNames.slice(1)],
      });
      const listDefinition = createListDefinition({ listStyles });
      state.mutableListDefinitions[nextListInfo.listProps.listId] = listDefinition;
    }

    /**
     * Every time we nest a list, we increment the list level.
     */
    if (isList(childNode)) {
      const listLevelType = isUnorderedList(childNode) ? 'ul' : 'ol';
      /**
       * This is mutableListLevelTypes is hackish, but is required to fit the
       * current way the html-to-aline converter works. This is because there
       * isn't a way to pass anything except up the tree except `blocks`.
       *
       * We are passing down a mutable `listLevelTypes` array which keeps track
       * of what the list types (ul or ol) are at each depth of the list.
       *
       * When the value exists, we keep passing it through to the children and
       * we use this array, along with the `listLevel` value to mutate, in
       * place, the type at each level.
       *
       * Some limitations of this are that we cannot support both `ul` and `ol`
       * at the same level.
       *
       * Although we could fix this particular issue by creating different list
       * definitions, this would come at, IMHO, the much higher cost of having
       * the lists break in terms of their UI. In particular, sequence ordering
       * can easily break if we start creating new list definitions for the same
       * list.
       */
      const nextListInfo = getNextListInfo(state, 1, listLevelType);
      nextListInfo.mutableListLevelTypes[nextListInfo.listProps.listLevel] = listLevelType;
      const nextBlocks = convertNodeToBlocks(childNode, {
        ...state,
        mutableListLevelTypes: nextListInfo.mutableListLevelTypes,
        nextParagraph: {
          ...state.nextParagraph,
          indentationFirstLine: listIndentationFirstLine,
          indentationLeft: state.nextParagraph.indentationLeft + listIndentationLeftIncrement,
          listProps: nextListInfo.listProps,
        },
      }).blocks;
      if (nextListInfo.isNewList) {
        createListDefinitionInEditor(nextListInfo);
      }
      return { ...state, blocks: nextBlocks } satisfies Context;
    }

    /**
     * When we encounter a list item, we don't increment the list level.
     */
    if (isListItem(childNode)) {
      const nextListInfo = getNextListInfo(state, 0, 'ul');

      const nextBlocks = convertNodeToBlocks(childNode, {
        ...state,
        mutableListLevelTypes: nextListInfo.mutableListLevelTypes,
        nextParagraph: {
          ...state.nextParagraph,
          indentationFirstLine: listIndentationFirstLine,
          indentationLeft: state.nextParagraph.indentationLeft,
          listProps: nextListInfo.listProps,
        },
        paragraph: null,
      }).blocks;

      /**
       * Technically, we shouldn't have to do this because an `li` is always
       * nested inside a list. However, we do this because people don't always
       * form their HTML correctly and a missing list definition will result in
       * a crash.
       */
      if (nextListInfo.isNewList) createListDefinitionInEditor(nextListInfo);

      return { ...state, blocks: nextBlocks } satisfies Context;
    }

    const prevSibling = getPreviousContentSibling(childNode);

    // If the previous sibling is not an inline element, we need to create a new paragraph.
    if (prevSibling && !isInline(prevSibling)) {
      const newParagraph = createDefaultParagraph({
        paraProps: {
          ...state.nextParagraph,
          children: [],
          locationId: generateLocationId(),
          spacingAfter: HTML_PARAGRAPH_SPACING_PT.default,
        },
      });
      const newParagraphFormats: TextPropsWithoutComments = { ...state.nextFormats };

      return {
        ...state,
        blocks: convertNodeToBlocks(childNode, {
          ...state,
          blocks: [...state.blocks, newParagraph],
          nextFormats: newParagraphFormats,
          paragraph: newParagraph,
        }).blocks,
      } satisfies Context;
    }

    return {
      ...state,
      blocks: convertNodeToBlocks(childNode, state).blocks,
    } satisfies Context;
  }, context);

  /**
   * If the last block is a paragraph and has no children, we need to add a
   * default text node to it or editor will crash.
   */
  const lastBlock = finalState.blocks.at(finalState.blocks.length - 1);
  if (lastBlock && lastBlock.type === 'paragraph') {
    if (lastBlock.children.length === 0) {
      lastBlock.children.push({
        ...finalState.nextFormats,
        comments: [],
        suggestedEdits: [],
        text: '',
      });
    }
  }

  return {
    blocks: finalState.blocks,
    listDefinitions: finalState.mutableListDefinitions,
  };
}

function findOrCreateLastParagraph(context: Context): ParagraphElement {
  if (context.paragraph) return context.paragraph;

  const newParagraph = createDefaultParagraph({
    paraProps: {
      ...context.nextParagraph,
      children: [],
      locationId: generateLocationId(),
      spacingAfter: HTML_PARAGRAPH_SPACING_PT.default,
    },
  });
  context.blocks.push(newParagraph);
  return newParagraph;
}

function getPreviousContentSibling(node: Node): Node | null {
  let previousSibling = node.previousSibling;
  while (previousSibling && getIsIgnorableTextNode(previousSibling)) {
    previousSibling = previousSibling.previousSibling;
  }

  return previousSibling;
}

function getIsIgnorableTextNode(node: Node) {
  if (!getIsTextNode(node)) return false;

  const { shouldPreserve, text } = cleanTextContent(node.textContent ?? '');
  return !text && !shouldPreserve;
}

const alineLinkTextFormatting = {
  fontColor: { color: linkColor, origin: 'USER_APPLIED' },
  underline: { active: true, origin: 'USER_APPLIED' },
} satisfies Pick<AlineText, 'fontColor' | 'underline'>;

function applyAlineLinkTextFormatting(text: AlineText) {
  return {
    ...text,
    ...alineLinkTextFormatting,
  };
}

function traverseInlineContent(
  node: Node,
  nextFormats: TextPropsWithoutComments,
  preserveHtmlTextStyles: boolean
): AlineText[] {
  const result: AlineText[] = [];
  if (getIsTextNode(node)) {
    const { shouldPreserve, text } = cleanTextContent(node.textContent ?? '');
    if (text || shouldPreserve) {
      const alineText: AlineText = {
        ...getMarks(node, nextFormats, preserveHtmlTextStyles),
        comments: [],
        suggestedEdits: [],
        text,
      };
      result.push(alineText);
    }
  } else if (getIsHtmlElementNode(node)) {
    for (const childNode of Array.from(node.childNodes)) {
      result.push(...traverseInlineContent(childNode, nextFormats, preserveHtmlTextStyles));
    }
  }
  return result;
}
